import React, { useState, useEffect } from "react";
import { BASE_URL } from "@/lib/API";
import { Modal, DetailsWorkspaceModal } from "../../../../components/ui"; // updated by nithin
import { useLeadActivities } from "../hooks/useLeadActivities"; // nithinok
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { StatusAssignmentPanel } from "./StatusAssignmentPanel";
import { STATUS_VARIANT_MAP } from "@/constants/globalConstants";
import { fmtDate } from "@/lib/formatters";
import { CustomerDetailsInfoGrid } from "../../../verve-contacts/components/CustomerDetailsInfoGrid";
import { Avatar, Badge } from "../../../../components/ui";
import { SectionTitle, BodyText, SubText, Caption } from "@/components/ui/Typography";
import { Calendar, User, Eye, Download, UserX, UserCheck, AlertTriangle, FileText, Mail, Phone, MapPin, Briefcase, Tag, Home, IndianRupee, Link, Building } from "lucide-react"; // updated by nithin
import { QuotationPreviewModal } from "./QuotationPreviewModal";

export const ViewLead = ({ isOpen, onClose, lead, user, stages = [], teamMembers = [], projectManagers = [], updateStatus, updateLeadState, onToggleDisable }) => {
  const { activities, loading: activitiesLoading, addActivity, refreshActivities } = useLeadActivities(lead?._id, user);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [localLead, setLocalLead] = useState(lead);

  useEffect(() => {
    if (lead) setLocalLead(lead);
  }, [lead]);

  // Find the initial note from activities history
  const acts = Array.isArray(activities) ? activities : [];
  const initialActivity = acts.find(a => a.action === 'Contact Created' || a.action === 'Lead Created');
  const initialNote = initialActivity?.note || initialActivity?.comment || localLead?.notes;
  const lastActivity = acts.length > 0 ? acts[0] : null;
  const lastNoteRaw = lastActivity?.note || lastActivity?.comment || localLead?.notes || "";
  const lastNote = lastNoteRaw.includes(' | Note: ') ? lastNoteRaw.split(' | Note: ')[1] : lastNoteRaw;

  const role = user?.role?.toLowerCase()?.trim()?.replace(/\s+/g, '-');
  const isSalesExec = role === 'sales-exec' || role === 'sale-exec';
  const isSalesHead = role === 'sales-head' || role === 'sale-head';
  const isOwner = role === 'owner';
  const isSuperAdmin = ['admin', 'owner', 'Verve-admin'].includes(role);

  const salesExecs = (teamMembers || []).filter(u => {
    const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    const isExec = uRole === 'sales-exec' || uRole === 'sale-exec' || uRole === 'sales-executive' || uRole === 'sales';
    const leadLoc = (localLead?.city || localLead?.location || "").toLowerCase().trim();
    const userLoc = (u.workLocation || u.officeLocation || "").toLowerCase().trim();
    return isExec && (!leadLoc || !userLoc || userLoc.includes(leadLoc) || leadLoc.includes(userLoc));
  });

  const isAssignedToSales = !!(localLead?.assignedTo?._id || localLead?.assignedTo);
  const isAssignedToPM = !!(localLead?.projectManager?._id || localLead?.projectManager);

  const handleAddActivity = async () => {
    if (!note.trim()) return;
    setIsSubmitting(true);
    try {
      await addActivity(note, "Interested");
      setNote("");
      refreshActivities();
    } catch (err) { } finally { setIsSubmitting(false); }
  };

  const handleStatusChange = async (newStatus) => {
    await updateStatus(localLead, newStatus);
    refreshActivities();
  };

  const handleAssignmentChange = async (userId) => {
    try {
      await updateLeadState(localLead._id, { assignedTo: userId, status: 'assigned' });
      showSuccessMessage("Lead assigned successfully");
      refreshActivities();
    } catch (err) { showApiError(err); }
  };

  const handlePMChange = async (userId) => {
    try {
      await updateLeadState(localLead._id, { projectManager: userId });
      showSuccessMessage("Project Manager updated");
      refreshActivities();
    } catch (err) { showApiError(err); }
  };

  const handleDropLead = async () => {
    // Confirmation is handled by ChangeStatusForm via updateStatus
    try {
      await updateStatus(localLead, "drop");
      // onClose(); // Don't close yet, let the user fill the drop reason
    } catch (e) { showApiError(e); }
  };

  const handleToggleDisable = async () => {
    // Confirmation is handled by LeadsScreen.onToggleDisable
    try {
      await onToggleDisable(localLead);
      setLocalLead(prev => ({ ...prev, isDisabled: !prev.isDisabled }));
      refreshActivities();
    } catch (err) {
      showApiError(err);
    }
  };

  const handlePreviewQuotation = () => {
    setIsPreviewOpen(true);
  };

  if (!localLead) return null;

  const displayName = localLead.customerName || `${localLead.firstName || ""} ${localLead.lastName || ""}`.trim() || "Lead";

  // --- updated by nithin ---
  const leadFields = [
    { icon: User, label: "Full Name", value: displayName },
    { icon: Tag, label: "Lead ID", value: (localLead.customId || "").toUpperCase() },
    { icon: Mail, label: "Email Address", value: localLead.email || "—" },
    { icon: Phone, label: "Phone Number", value: localLead.phoneNumber || localLead.phone || "—" },
    { icon: Briefcase, label: "Lead Status", value: localLead.leadStatus?.replace('_', ' ') || "—" },
    { icon: Calendar, label: "Created At", value: fmtDate(localLead.createdAt) },
    localLead.address1 && { icon: Home, label: "Address Line 1", value: localLead.address1 },
    localLead.address2 && { icon: Home, label: "Address Line 2", value: localLead.address2 },
    localLead.city && { icon: MapPin, label: "City", value: localLead.city },
    localLead.district && { icon: MapPin, label: "District", value: localLead.district },
    localLead.state && { icon: MapPin, label: "State", value: localLead.state },
    localLead.pincode && { icon: MapPin, label: "PIN Code", value: localLead.pincode },
    localLead.officeLocation && { icon: Building, label: "Office Location", value: localLead.officeLocation },
    localLead.referenceType && { icon: Link, label: "Reference Source", value: localLead.referenceType },
    localLead.referenceDetails && { icon: Link, label: "Referral Details", value: localLead.referenceDetails },
    localLead.requirementType && { icon: Building, label: "Property Type", value: localLead.requirementType },
    localLead.propertyType && { icon: Home, label: "Sub Type", value: localLead.propertyType },
    localLead.budget && { icon: IndianRupee, label: "Estimated Budget", value: `₹${new Intl.NumberFormat('en-IN').format(localLead.budget)}` },
  ].filter(Boolean);

  const leadTimeline = acts
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
    .map((a, i) => {
      const content = a.note || a.comment || "";
      const text = content.includes(' | Note: ') ? content.split(' | Note: ')[1] : content;
      const action = a.action ? `${a.action}: ` : "";
      const time = a.createdAt ? new Date(a.createdAt).toLocaleString('en-IN') : "";
      return (
        <span key={i} className="flex flex-col gap-0.5">
           <span className="font-bold text-slate-700">{action}{text}</span>
           <span className="text-[9px] text-slate-400">{time} by {a.performedBy?.name || 'System'}</span>
        </span>
      );
    });

  const handleDetailsNote = async (draftText) => {
    if (!draftText.trim()) return;
    setIsSubmitting(true);
    try {
      await addActivity(draftText, "Interested");
      refreshActivities();
    } catch (err) { } finally { setIsSubmitting(false); }
  };

  return (
    <>
      <DetailsWorkspaceModal
        isOpen={isOpen}
        onClose={onClose}
        title="Verve Kitchens"
        subtitle="LEAD INTERACTION DETAILS"
        fields={leadFields}
        timeline={leadTimeline}
        onAddNote={handleDetailsNote}
        isInactive={localLead.isDisabled}
        statusLabel={(isSuperAdmin || isSalesHead) ? (localLead.isDisabled ? "Enable Lead" : "Disable Lead") : null}
        statusColor={localLead.isDisabled ? "emerald" : "rose"}
        onStatusToggle={(isSuperAdmin || isSalesHead) ? handleToggleDisable : null}
        saving={isSubmitting}
        savingNote={isSubmitting}
      >
        <div className="flex flex-col gap-6">
          {/* Appointment Section */}
          {localLead.appointmentDate && (
            <div className="bg-orange-50/40 rounded-xl p-4 border border-orange-100/60">
              <div className="text-[10px] text-orange-600 font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                <Calendar size={14} className="text-orange-500" />
                Scheduled Appointment
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <SectionTitle className="text-[14px] text-orange-800">
                    {new Date(localLead.appointmentDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </SectionTitle>
                  <Caption className="text-orange-600 font-medium">Please ensure the team is ready for the visit.</Caption>
                </div>
              </div>
            </div>
          )}

          {/* Site Survey / Inspection Section */}
          {(localLead.floorDetails || localLead.existingPowerConsumption) && (
            <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-100">
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Site Survey Findings
              </div>
              <div className="grid grid-cols-2 gap-4">
                {localLead.floorDetails && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Floor Details</Caption>
                    <p className="text-[12px] font-medium text-slate-700">{localLead.floorDetails}</p>
                  </div>
                )}
                {localLead.existingPowerConsumption && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Consumption</Caption>
                    <p className="text-[12px] font-medium text-slate-700">{localLead.existingPowerConsumption} Units/mo</p>
                  </div>
                )}
                {localLead.VerveCapacityRequested && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Recommended Capacity</Caption>
                    <p className="text-[12px] font-medium text-slate-700">{localLead.VerveCapacityRequested} kW</p>
                  </div>
                )}
                {localLead.existingPowerPaid && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Last Bill Amount</Caption>
                    <p className="text-[12px] font-medium text-slate-700">₹{localLead.existingPowerPaid}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quotation Section */}
          {(localLead.quotationAmount || localLead.VerveCapacity || localLead.quotationFile?.url) && (
            <div className="bg-indigo-50/30 rounded-xl p-4 border border-indigo-100/50">
              <div className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                Quotation Details
              </div>
              <div className="grid grid-cols-2 gap-4">
                {localLead.quotationAmount && (
                  <div>
                    <Caption className="text-[10px] font-bold text-indigo-400">Quoted Amount</Caption>
                    <p className="text-[12px] font-bold text-indigo-700">₹{new Intl.NumberFormat('en-IN').format(localLead.quotationAmount)}</p>
                  </div>
                )}
                {localLead.VerveCapacity && (
                  <div>
                    <Caption className="text-[10px] font-bold text-indigo-400">System Capacity</Caption>
                    <p className="text-[12px] font-bold text-indigo-700">{localLead.VerveCapacity} kW</p>
                  </div>
                )}
                {localLead.hasSubsidy && (
                  <div className="col-span-2">
                    <Caption className="text-[10px] font-bold text-indigo-400">Subsidy Status</Caption>
                    <p className="text-[12px] font-bold text-indigo-700 uppercase">
                      {localLead.hasSubsidy === 'yes' ? `Eligible (ID: ${localLead.subsidyId || 'N/A'})` : 'Not Applicable'}
                    </p>
                  </div>
                )}

                {/* Quotation PDF Link */}
                {localLead.quotationFile?.url && (
                  <div className="col-span-2 mt-2 pt-2 border-t border-indigo-100/50">
                    <div className="flex items-center gap-2">
                      <a
                        href={`${BASE_URL}${localLead.quotationFile.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-all shadow-sm text-[10px] font-bold uppercase tracking-wider"
                      >
                        <Eye size={12} />
                        View PDF
                      </a>
                      <a
                        href={`${BASE_URL}${localLead.quotationFile.url}`}
                        download={localLead.quotationFile.name}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50 transition-all shadow-sm text-[10px] font-bold uppercase tracking-wider"
                      >
                        <Download size={12} />
                        Download
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents & Photos Section */}
          {localLead.files && localLead.files.length > 0 && (
            <div className="bg-slate-50/30 rounded-xl p-4 border border-slate-100/50">
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Documents & Photos
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {localLead.files.map((file, idx) => {
                  const isImage = file.type?.startsWith('image/');
                  const isPdf = file.type === 'application/pdf';
                  const fileUrl = `${BASE_URL}${file.url}`;

                  return (
                    <div
                      key={idx}
                      className="group flex items-center gap-2 bg-white p-1.5 rounded-lg border border-slate-100 hover:border-brand-blue/30 transition-all shadow-sm"
                    >
                      <div className="w-10 h-10 rounded-md bg-slate-50 flex items-center justify-center overflow-hidden shrink-0 border border-slate-50">
                        {isImage ? (
                          <img src={fileUrl} alt={file.name} className="w-full h-full object-cover" />
                        ) : isPdf ? (
                          <div className="flex flex-col items-center">
                            <span className="text-sm">📄</span>
                            <Caption className="text-[6px] font-bold text-red-500 uppercase">PDF</Caption>
                          </div>
                        ) : (
                          <span className="text-sm">📁</span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-slate-700 truncate leading-tight mb-0.5" title={file.name}>
                          {file.name}
                        </p>
                        <Caption className="text-[8px] text-slate-400">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Caption>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pr-0.5">
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-brand-blue/10 hover:text-brand-blue transition-all"
                          title="View"
                        >
                          <Eye size={12} />
                        </a>
                        <a
                          href={fileUrl}
                          download={file.name}
                          className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                          title="Download"
                        >
                          <Download size={12} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <StatusAssignmentPanel
            lead={localLead}
            stages={stages}
            salesExecs={salesExecs}
            projectManagers={projectManagers}
            isSalesExec={isSalesExec}
            isSalesHead={isSalesHead}
            isOwner={isOwner}
            isSuperAdmin={isSuperAdmin}
            isAssignedToSales={isAssignedToSales}
            isAssignedToPM={isAssignedToPM}
            onStatusChange={handleStatusChange}
            onAssignmentChange={handleAssignmentChange}
            onPMChange={handlePMChange}
          />

          {/* Quotation Download Button */}
          {/* 
          <div className="pt-4 border-t border-slate-100/60 mt-2">
            <button
              onClick={handlePreviewQuotation}
              className="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-wider text-[11px] shadow-sm hover:bg-black transition-all group"
            >
              <FileText size={16} className="text-brand-orange group-hover:scale-110 transition-transform" />
              Preview and download Quotation
            </button>
          </div>
          */}
        </div>
      </DetailsWorkspaceModal>

      {/* Render Quotation Preview Modal */}
      <QuotationPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        lead={localLead}
      />
    </>
  );
  // --- end updated by nithin ---

  /*
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Lead Interaction Details" size="xl" className="!rounded-md">
      <div className="flex flex-col lg:grid lg:grid-cols-5 gap-7">
        {/* Left: Info Grid * /}
        <div className="lg:col-span-3 space-y-6">
          <div className="flex items-center gap-5 pb-5 border-b border-slate-100/60">
            <Avatar name={displayName} size="w-14 h-14 text-xl shadow-sm" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <SectionTitle className="text-[17px] tracking-tight">{displayName}</SectionTitle>
                <Badge variant="bg-[#eff6ff] text-brand-blue border-blue-100" label={(localLead.customId || "").toUpperCase()} className="uppercase tracking-widest text-[9px] px-2.5 py-0.5 rounded-md font-bold" />
              </div>
              {(!localLead.email ||
                localLead.email.trim() === "" ||
                localLead.email.toLowerCase() === "n/a" ||
                localLead.email.toLowerCase() === "undefined" ||
                localLead.email.toLowerCase() === "null") ? (
                <div className="flex items-center gap-1.5 text-rose-500 mb-2">
                  <AlertTriangle size={12} />
                  <SubText className="text-[11px] font-bold">Email missing - Auto-delivery disabled</SubText>
                </div>
              ) : (
                <SubText className="text-[12px] font-medium mb-2 text-slate-400">
                  {localLead.email}
                </SubText>
              )}
              <div className="flex">
                <Badge
                  variant={STATUS_VARIANT_MAP[localLead.leadStatus?.toLowerCase()] || "bg-slate-50 text-slate-500"}
                  label={localLead.leadStatus === 'new' ? 'New Lead' : localLead.leadStatus?.replace('_', ' ')}
                  className="rounded-md px-2.5 py-1 text-[10px]  tracking-widest font-bold shadow-sm"
                />
                {localLead.isDisabled && (
                  <Badge
                    variant="danger"
                    label="Disabled"
                    className="rounded-md px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold shadow-sm ml-2"
                  />
                )}
              </div>
            </div>
            {(isSuperAdmin || isSalesHead) && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDropLead}
                  title="Drop Lead"
                  className="p-2 rounded-md transition-all shadow-sm border bg-slate-50 text-slate-400 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-100"
                >
                  <AlertTriangle size={18} />
                </button>
                <button
                  onClick={handleToggleDisable}
                  title={localLead.isDisabled ? "Enable Lead" : "Disable Lead"}
                  className={`p-2 rounded-md transition-all shadow-sm border ${localLead.isDisabled
                      ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                      : "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                    }`}
                >
                  {localLead.isDisabled ? <UserCheck size={18} /> : <UserX size={18} />}
                </button>
              </div>
            )}
          </div>

          <div className="py-1">
            <CustomerDetailsInfoGrid
              detail={{ ...localLead, notes: lastNote }}
              notesLabel="Last Notes"
            />
          </div>

          {/* Appointment Section * /}
          {localLead.appointmentDate && (
            <div className="bg-orange-50/40 rounded-xl p-4 border border-orange-100/60">
              <div className="text-[10px] text-orange-600 font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                <Calendar size={14} className="text-orange-500" />
                Scheduled Appointment
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <SectionTitle className="text-[14px] text-orange-800">
                    {new Date(localLead.appointmentDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </SectionTitle>
                  <Caption className="text-orange-600 font-medium">Please ensure the team is ready for the visit.</Caption>
                </div>
              </div>
            </div>
          )}

          {/* Site Survey / Inspection Section * /}
          {(localLead.floorDetails || localLead.existingPowerConsumption) && (
            <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-100">
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Site Survey Findings
              </div>
              <div className="grid grid-cols-2 gap-4">
                {localLead.floorDetails && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Floor Details</Caption>
                    <p className="text-[12px] font-medium text-slate-700">{localLead.floorDetails}</p>
                  </div>
                )}
                {localLead.existingPowerConsumption && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Consumption</Caption>
                    <p className="text-[12px] font-medium text-slate-700">{localLead.existingPowerConsumption} Units/mo</p>
                  </div>
                )}
                {localLead.VerveCapacityRequested && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Recommended Capacity</Caption>
                    <p className="text-[12px] font-medium text-slate-700">{localLead.VerveCapacityRequested} kW</p>
                  </div>
                )}
                {localLead.existingPowerPaid && (
                  <div>
                    <Caption className="text-[10px] font-bold text-slate-400">Last Bill Amount</Caption>
                    <p className="text-[12px] font-medium text-slate-700">₹{localLead.existingPowerPaid}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quotation Section * /}
          {(localLead.quotationAmount || localLead.VerveCapacity || localLead.quotationFile?.url) && (
            <div className="bg-indigo-50/30 rounded-xl p-4 border border-indigo-100/50">
              <div className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                Quotation Details
              </div>
              <div className="grid grid-cols-2 gap-4">
                {localLead.quotationAmount && (
                  <div>
                    <Caption className="text-[10px] font-bold text-indigo-400">Quoted Amount</Caption>
                    <p className="text-[12px] font-bold text-indigo-700">₹{new Intl.NumberFormat('en-IN').format(localLead.quotationAmount)}</p>
                  </div>
                )}
                {localLead.VerveCapacity && (
                  <div>
                    <Caption className="text-[10px] font-bold text-indigo-400">System Capacity</Caption>
                    <p className="text-[12px] font-bold text-indigo-700">{localLead.VerveCapacity} kW</p>
                  </div>
                )}
                {localLead.hasSubsidy && (
                  <div className="col-span-2">
                    <Caption className="text-[10px] font-bold text-indigo-400">Subsidy Status</Caption>
                    <p className="text-[12px] font-bold text-indigo-700 uppercase">
                      {localLead.hasSubsidy === 'yes' ? `Eligible (ID: ${localLead.subsidyId || 'N/A'})` : 'Not Applicable'}
                    </p>
                  </div>
                )}

                {/* Quotation PDF Link * /}
                {localLead.quotationFile?.url && (
                  <div className="col-span-2 mt-2 pt-2 border-t border-indigo-100/50">
                    <div className="flex items-center gap-2">
                      <a
                        href={`${BASE_URL}${localLead.quotationFile.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-all shadow-sm text-[10px] font-bold uppercase tracking-wider"
                      >
                        <Eye size={12} />
                        View PDF
                      </a>
                      <a
                        href={`${BASE_URL}${localLead.quotationFile.url}`}
                        download={localLead.quotationFile.name}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50 transition-all shadow-sm text-[10px] font-bold uppercase tracking-wider"
                      >
                        <Download size={12} />
                        Download
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents & Photos Section * /}
          {localLead.files && localLead.files.length > 0 && (
            <div className="bg-slate-50/30 rounded-xl p-4 border border-slate-100/50">
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Documents & Photos
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {localLead.files.map((file, idx) => {
                  const isImage = file.type?.startsWith('image/');
                  const isPdf = file.type === 'application/pdf';
                  const fileUrl = `${BASE_URL}${file.url}`;

                  return (
                    <div
                      key={idx}
                      className="group flex items-center gap-2 bg-white p-1.5 rounded-lg border border-slate-100 hover:border-brand-blue/30 transition-all shadow-sm"
                    >
                      <div className="w-10 h-10 rounded-md bg-slate-50 flex items-center justify-center overflow-hidden shrink-0 border border-slate-50">
                        {isImage ? (
                          <img src={fileUrl} alt={file.name} className="w-full h-full object-cover" />
                        ) : isPdf ? (
                          <div className="flex flex-col items-center">
                            <span className="text-sm">📄</span>
                            <Caption className="text-[6px] font-bold text-red-500 uppercase">PDF</Caption>
                          </div>
                        ) : (
                          <span className="text-sm">📁</span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-slate-700 truncate leading-tight mb-0.5" title={file.name}>
                          {file.name}
                        </p>
                        <Caption className="text-[8px] text-slate-400">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Caption>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pr-0.5">
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-brand-blue/10 hover:text-brand-blue transition-all"
                          title="View"
                        >
                          <Eye size={12} />
                        </a>
                        <a
                          href={fileUrl}
                          download={file.name}
                          className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                          title="Download"
                        >
                          <Download size={12} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <StatusAssignmentPanel
            lead={localLead}
            stages={stages}
            salesExecs={salesExecs}
            projectManagers={projectManagers}
            isSalesExec={isSalesExec}
            isSalesHead={isSalesHead}
            isOwner={isOwner}
            isSuperAdmin={isSuperAdmin}
            isAssignedToSales={isAssignedToSales}
            isAssignedToPM={isAssignedToPM}
            onStatusChange={handleStatusChange}
            onAssignmentChange={handleAssignmentChange}
            onPMChange={handlePMChange}
          />

          {/* Quotation Download Button * /}
          <div className="pt-4 border-t border-slate-100/60 mt-2">
            <button
              onClick={handlePreviewQuotation}
              className="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-wider text-[11px] shadow-sm hover:bg-black transition-all group"
            >
              <FileText size={16} className="text-brand-orange group-hover:scale-110 transition-transform" />
              Preview and download Quotation
            </button>
          </div>
        </div>

        {/* Right: Interaction History * /}
        <div className="lg:col-span-2 flex flex-col h-full border-l border-slate-100/60 lg:pl-6">
          <div className="flex items-center justify-between mb-5">
            <SectionTitle className="flex items-center gap-2 text-[15px]">
              History
              <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" />
            </SectionTitle>
          </div>

          <div className="flex gap-2 mb-6">
            <input
              className="flex-1 h-11 pl-4 rounded-md bg-white border border-slate-200 text-[12px] font-medium text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-blue/10 transition-all shadow-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add activity note..."
              onKeyDown={(e) => e.key === 'Enter' && handleAddActivity()}
              disabled={isSubmitting}
            />
            <button
              className={`w-11 h-11 flex items-center justify-center text-white rounded-md transition-all active:scale-95 shrink-0 shadow-sm ${isSubmitting ? 'bg-slate-300' : 'bg-[#f97316] hover:bg-[#ea580c]'}`}
              onClick={handleAddActivity}
              disabled={isSubmitting}
            >
              <span className="text-xl font-medium leading-none">+</span>
            </button>
          </div>

          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
            {acts
              .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
              .map((a, i) => {
                const isFirst = i === 0;
                return (
                  <div key={i} className="relative pl-7 group">
                    <div className="absolute left-[5.5px] top-4 bottom-0 w-[1px] bg-slate-100 group-last:hidden" />
                    <div className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 border-white shadow-sm z-10 transition-all ${isFirst ? "bg-emerald-500 ring-4 ring-emerald-50" : "bg-slate-300 ring-4 ring-slate-50"}`} />
                    <div className={`rounded-md p-3.5 border transition-all duration-300 ${isFirst ? "bg-[#f0fdf4]/60 border-emerald-100 shadow-sm" : "bg-white border-slate-100 hover:border-slate-200 shadow-sm"}`}>
                      <div className="space-y-1.5 mb-3">
                        {a.action && <p className="font-bold text-xs uppercase tracking-tight text-slate-800">{a.action}</p>}
                        {(() => {
                          const content = a.note || a.comment || "";
                          if (content.includes(' | Note: ')) {
                            const [systemPart, userPart] = content.split(' | Note: ');
                            return (
                              <>
                                <p className="text-[10px] text-slate-400 font-medium leading-tight">{systemPart}</p>
                                <p className={`font-semibold ${isFirst ? "text-emerald-900" : "text-slate-700"}`}>
                                  <span className="text-[10px]  text-slate-400 mr-1.5">Note:</span>
                                  {userPart}
                                </p>
                              </>
                            );
                          }
                          return <p className="text-[11.5px] font-medium text-slate-700 leading-relaxed">{content}</p>;
                        })()}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <Caption className="text-slate-400 font-bold text-[9px]">{fmtDate(a.createdAt)}</Caption>
                          <Caption className="text-slate-300 font-semibold text-[8px] uppercase tracking-tighter">{a.createdAt ? new Date(a.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</Caption>
                        </div>
                        {a.performedBy?.name && (
                          <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md border border-slate-100 bg-slate-50 shadow-sm">
                            <Caption className="font-bold text-[8.5px] text-brand-blue">{a.performedBy.name.split(' ')[0]}</Caption>
                            <div className="w-4 h-4 rounded bg-white flex items-center justify-center border border-slate-100">
                              <User size={8} className="text-brand-blue" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Render Quotation Preview Modal * /}
      <QuotationPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        lead={localLead}
      />
    </Modal>
  );
  */
};
